import type { SkillSlug, TierLevel } from '../../api/types';

/**
 * Journey presentation constants — the 4 ordered tiers + the skill axis labels.
 *
 * These MIRROR the locked domain (`services/shared/domain/skill.py`):
 *   - TierLevel 1..4 maps to Beginner / Intermediate / Advanced / Expert.
 *   - The skill axis REUSES `ScenarioDomain` (nine slugs); labels match the
 *     scenarios catalog (`routes/scenarios.index.tsx#DOMAIN_LABELS`).
 *
 * Kept tiny + pure so both routes and the AppShell badge compose from one
 * source of truth rather than re-deriving labels.
 */

import type { BadgeVariant } from '../ui';

export interface TierMeta {
  level: TierLevel;
  /** Short code shown in the badge, e.g. "T2". */
  code: string;
  /** Human label, e.g. "Intermediate". */
  label: string;
  variant: BadgeVariant;
}

export const TIERS: Record<TierLevel, TierMeta> = {
  1: { level: 1, code: 'T1', label: 'Beginner', variant: 'neutral' },
  2: { level: 2, code: 'T2', label: 'Intermediate', variant: 'info' },
  3: { level: 3, code: 'T3', label: 'Advanced', variant: 'warn' },
  4: { level: 4, code: 'T4', label: 'Expert', variant: 'success' },
};

export const TIER_LEVELS: ReadonlyArray<TierLevel> = [1, 2, 3, 4];

export function tierMeta(level: TierLevel): TierMeta {
  return TIERS[level] ?? TIERS[1];
}

export const SKILL_LABELS: Record<SkillSlug, string> = {
  'threat-hunting': 'Threat Hunting',
  'incident-response': 'Incident Response',
  forensics: 'Forensics',
  'malware-analysis': 'Malware Analysis',
  'ot-ics': 'OT / ICS',
  'soc-ops': 'SOC Ops',
  'network-defense': 'Network Defense',
  'cloud-security': 'Cloud Security',
  'purple-team': 'Purple Team',
};

export function skillLabel(slug: SkillSlug): string {
  return SKILL_LABELS[slug] ?? slug;
}

/** Highest tier across all skills — drives the AppShell summary badge. */
export function peakTier(
  tiers: ReadonlyArray<{ highestUnlockedTierLevel: TierLevel }>,
): TierLevel {
  let peak: TierLevel = 1;
  for (const t of tiers) {
    if (t.highestUnlockedTierLevel > peak) peak = t.highestUnlockedTierLevel;
  }
  return peak;
}
